import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    Input,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    TelXLButtonComponent,
    TelXLDropdownComponent,
    TelXLLoadingComponent,
    TelXLSearchComponent,
} from '@dxp/elements';
import {
    ICustomerDetails,
    ICustomerDirectory,
    ICustomerDirectoryEvent,
    IDirectoryListing,
    IDirectoryListingEvent,
    IDirectorySelection,
    IDropdown,
    NBU_ID,
} from '@dxp/core/models';
import {
    ContactService,
    CustomerDirectoryService,
    PermissionService,
} from '@dxp/core/services';
import { fromEvent } from 'rxjs';
import { debounceTime, filter, map, tap } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ListingMode, MediaOptions, MediaType } from '@dxp/core/types';
import { BUSINESSUNIT_STORE } from '@dxp/core/stores';
import { TelXLDirectoryListingItemComponent } from '../item/directory-listing-item.component';
import { TelXlEndCustomerDetailsFormComponent } from './customer-details-form/customer-details-form.component';

const LIMIT = 20;
const ALL_BUSINESS_UNITS = '<ALL>';

@Component({
    selector: 'telxl-shared-customer-directory-panel',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        TelXLLoadingComponent,
        TelXLDirectoryListingItemComponent,
        TelXLSearchComponent,
        TelXLDropdownComponent,
        TelXLButtonComponent,
        TelXlEndCustomerDetailsFormComponent,
    ],
    templateUrl: './customer-directory-panel.component.html',
    styleUrl: './customer-directory-panel.component.scss',
})
export class TelXLCustomerDirectoryPanelComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    readonly #destroyRef = inject(DestroyRef);
    readonly #customerDirectoryService = inject(CustomerDirectoryService);
    readonly #businessUnitStore = inject(BUSINESSUNIT_STORE);
    readonly #permissionService = inject(PermissionService);
    #contactService = inject(ContactService);

    #resizeObserver!: ResizeObserver;
    readonly #requiresEmail = signal<boolean>(false);
    readonly #requiresPhone = signal<boolean>(false);
    readonly #requiresName = signal<boolean>(false);
    readonly #businessUnit = signal<string>(ALL_BUSINESS_UNITS);
    readonly #media = signal<MediaOptions>('All');

    @Input()
    set requiresEmail(value: boolean) {
        this.#requiresEmail.set(value);
    }

    @Input()
    set requiresPhone(value: boolean) {
        this.#requiresPhone.set(value);
    }

    @Input()
    set requiresName(value: boolean) {
        this.#requiresName.set(value);
    }

    @Input()
    set businessUnitId(value: string | null) {
        this.#businessUnit.set(value === null ? NBU_ID : value);
    }

    selected = input<IDirectorySelection>();
    highlight = input<string>();

    customButtonTemplate = input<TemplateRef<any>>();
    customerId = input<string>();

    height = input.required<string>();
    mode = input<ListingMode[]>([]);
    displayMediaIcons = input<MediaType[]>([]);
    showMediaFilter = input<boolean>(false);
    showBusinessUnitFilter = input<boolean>(false);
    showCreateCustomer = input<boolean>(false);
    disabled = input<boolean>(false);
    clicked = output<ICustomerDirectoryEvent>();

    @ViewChild('createCustomerContaner')
    createCustomerContaner!: ElementRef<HTMLDivElement>;

    @ViewChild('scrollContainer', { static: false })
    scrollContainer!: ElementRef;

    readonly #customerDirectory = signal<ICustomerDirectory>({
        count: 0,
        customerDirectory: [],
    });

    readonly canCreateCustomer = computed<boolean>(
        () => this.showCreateCustomer() && this.hasCreatePermission,
    );

    readonly createCustomer = signal<boolean>(false);
    readonly createCustomerContanerHeight = signal<number>(0);

    readonly searchText = signal<string>('');
    readonly isLoading = signal<boolean>(false);

    readonly calculateHeight = computed<string>(
        () =>
            `calc(${this.height()} - 100px - ${this.createCustomerContanerHeight()}px)`,
    );

    businessUnitMenuItems = computed<IDropdown<string>[]>(() => [
        {
            label: 'All Units',
            value: ALL_BUSINESS_UNITS,
            disabled: false,
        },
        ...this.#businessUnitStore
            .businessUnits()
            .map(bu => ({
                label: bu.name,
                value: bu.id,
                disabled: false,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
    ]);

    mediaMenuItems: IDropdown<MediaOptions>[] = [
        {
            label: 'All Media',
            value: 'All',
            disabled: false,
        },
        {
            label: 'With Voice',
            value: 'Voice',
            disabled: false,
        },
        {
            label: 'With Email',
            value: 'Email',
            disabled: false,
        },
    ];

    mappedBusinessUnit = computed<string | null | undefined>(() => {
        const businessUnit = this.#businessUnit();

        switch (businessUnit) {
            case ALL_BUSINESS_UNITS:
                return undefined;
            case NBU_ID:
                return null;
            default:
                return businessUnit;
        }
    });

    listings = computed<IDirectoryListing[]>(() =>
        this.#customerDirectory().customerDirectory.map(customer => {
            let phoneNumber = undefined;

            if (customer.phoneNumbers?.length) {
                const preferred =
                    customer.phoneNumbers.find(p => p.preferred) ??
                    customer.phoneNumbers[0];

                phoneNumber = preferred.number;
            }

            return {
                id: customer.customerId,
                name: customer.name ?? '',
                phoneNumber: phoneNumber,
                email: customer.email ? customer.email : undefined,
                object: JSON.stringify(customer),
            };
        }),
    );

    ngOnInit() {
        this.#loadCustomers(true);

        this.#contactService.contactUpdated$
            .pipe(
                filter(contactUpdate => contactUpdate.type === 'customers'),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe(contactUpdate =>
                this.refresh(contactUpdate.contact as ICustomerDetails),
            );
    }

    ngAfterViewInit() {
        if (this.scrollContainer) {
            fromEvent(this.scrollContainer.nativeElement, 'scroll')
                .pipe(
                    debounceTime(300),
                    map(() => this.scrollContainer.nativeElement),
                    filter(
                        container =>
                            container.scrollTop + container.clientHeight >=
                            container.scrollHeight - 50,
                    ),
                    tap(() => this.#loadCustomers(false)),
                    takeUntilDestroyed(this.#destroyRef),
                )
                .subscribe();
        }

        if (this.createCustomerContaner?.nativeElement) {
            this.#resizeObserver = new ResizeObserver(entries => {
                for (const entry of entries) {
                    this.createCustomerContanerHeight.set(
                        entry.contentRect.height,
                    );
                }
            });

            this.#resizeObserver.observe(
                this.createCustomerContaner?.nativeElement,
            );
        }
    }

    ngOnDestroy() {
        this.#resizeObserver.disconnect();
    }

    selectedChanged(event: IDirectoryListingEvent) {
        this.clicked.emit({
            type: event.type,
            item: this.#customerDirectory().customerDirectory.find(
                c => c.customerId === event.item.id,
            ) as ICustomerDetails,
        });
    }

    refresh(customer: ICustomerDetails) {
        this.#customerDirectory.update(current => {
            const index = current.customerDirectory.findIndex(
                c => c.customerId === customer.customerId,
            );

            if (index !== -1) {
                const updatedDirectory = [...current.customerDirectory];
                updatedDirectory[index] = customer;

                return {
                    count: current.count,
                    customerDirectory: updatedDirectory,
                };
            } else {
                return current;
            }
        });
    }

    #hasMoreData(): boolean {
        return (
            this.#customerDirectory().customerDirectory.length <
            this.#customerDirectory().count
        );
    }

    #loadCustomers(initialLoad: boolean) {
        if ((!initialLoad && !this.#hasMoreData()) || this.isLoading()) return;

        this.isLoading.set(true);

        this.#customerDirectoryService
            .fetchCustomerDirectory(
                LIMIT,
                this.#customerDirectory().customerDirectory.length,
                this.searchText().length > 0 ? this.searchText() : undefined,
                undefined,
                this.mappedBusinessUnit(),
                this.#requiresName(),
                this.#requiresEmail(),
                this.#requiresPhone(),
                this.customerId(),
            )
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(data => {
                this.#customerDirectory.update(current => {
                    return {
                        count: data.count,
                        customerDirectory: [
                            ...current.customerDirectory,
                            ...data.customerDirectory,
                        ],
                    };
                });

                this.isLoading.set(false);
            });
    }

    searchChanged(searchText: string) {
        this.#customerDirectory.set({
            count: 0,
            customerDirectory: [],
        });

        this.searchText.set(searchText);
        this.#loadCustomers(true);
    }

    createNewCustomer() {
        this.createCustomer.set(true);
    }

    completeCreateCustomer() {
        this.createCustomer.set(false);
        this.#loadCustomers(true);
    }

    cancelCreateCustomer() {
        this.createCustomer.set(false);
    }

    get businessUnit(): string {
        return this.#businessUnit() ?? ALL_BUSINESS_UNITS;
    }

    set businessUnit(value: string) {
        this.#businessUnit.set(value);

        this.#customerDirectory.set({
            count: 0,
            customerDirectory: [],
        });

        this.#loadCustomers(true);
    }

    get media(): MediaOptions {
        return this.#media();
    }

    set media(value: string) {
        this.#media.set(value as MediaOptions);

        switch (this.#media()) {
            case 'All': {
                this.#requiresEmail.set(false);
                this.#requiresPhone.set(false);
                break;
            }
            case 'Voice': {
                this.#requiresEmail.set(false);
                this.#requiresPhone.set(true);
                break;
            }
            case 'Email': {
                this.#requiresEmail.set(true);
                this.#requiresPhone.set(false);
                break;
            }
        }

        this.#customerDirectory.set({
            count: 0,
            customerDirectory: [],
        });

        this.#loadCustomers(true);
    }

    get hasCreatePermission(): boolean {
        return this.#permissionService.hasAnyPermission(['customer.create']);
    }
}
